import type { ReactElement } from 'react';
import { Button, DialogTrigger, Popover } from 'react-aria-components';
import { cx } from './cx';
import s from './CardHelp.module.css';

/** The two sentences every card, table, chart and KPI carries. Write real copy per card. */
export interface CardHelpContent {
  /** "What this shows": one or two sentences. */
  shows: string;
  /** "What you can do here": one or two sentences. */
  canDo: string;
}

export interface CardHelpProps extends CardHelpContent {
  /** What the card is about; the button reads "About {topic}". */
  topic: string;
  /** Use on dark grounds (navy cards, the hero). */
  onDark?: boolean;
}

/**
 * The small "?" next to a card heading. It opens a popover (not a modal) with "What this shows"
 * and "What you can do here". It closes on Escape, on a click outside, and when focus leaves it.
 */
export function CardHelp({ topic, shows, canDo, onDark = false }: CardHelpProps): ReactElement {
  const label = `About ${topic}`;
  return (
    <DialogTrigger>
      <Button aria-label={label} className={cx(s.trigger, onDark && s.onDark)}>
        <span className={s.dot} aria-hidden="true">
          ?
        </span>
      </Button>
      {/* With no nested Dialog, React Aria gives the popover itself role="dialog" and this label. */}
      <Popover placement="bottom start" offset={6} className={s.popover} aria-label={label}>
        <div className={s.dialog}>
          <p className={s.label}>What this shows</p>
          <p className={s.text}>{shows}</p>
          <p className={s.label}>What you can do here</p>
          <p className={s.text}>{canDo}</p>
        </div>
      </Popover>
    </DialogTrigger>
  );
}
